import type { LucideIcon } from 'lucide-react';
import { useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './SwipeableRow.module.css';

export type SwipeAction = {
  label: string;
  icon: LucideIcon;
  tone: 'success' | 'neutral' | 'danger';
  onTrigger: () => void;
};

export type SwipeableRowProps = {
  children: ReactNode;
  /** Revealed by swiping right (e.g. "Bought it"). */
  right?: SwipeAction;
  /** Revealed by swiping left (e.g. "More"). */
  left?: SwipeAction;
  className?: string;
};

const THRESHOLD = 88;

/**
 * Swipe gestures for phones. RULE: every swipe action must also exist as a visible control in
 * the row (checkbox, menu): swiping is a shortcut, never the only way.
 */
export function SwipeableRow({ children, right, left, className }: SwipeableRowProps) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const [dx, setDx] = useState(0);
  const locked = useRef<'x' | 'y' | null>(null);

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    start.current = { x: e.clientX, y: e.clientY };
    locked.current = null;
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!start.current) return;
    const mx = e.clientX - start.current.x;
    const my = e.clientY - start.current.y;
    if (!locked.current && (Math.abs(mx) > 8 || Math.abs(my) > 8))
      locked.current = Math.abs(mx) > Math.abs(my) ? 'x' : 'y';
    if (locked.current !== 'x') return;
    const limited = mx > 0 ? (right ? Math.min(mx, 140) : 0) : left ? Math.max(mx, -140) : 0;
    setDx(limited);
  };
  const onPointerUp = () => {
    if (dx >= THRESHOLD) right?.onTrigger();
    if (dx <= -THRESHOLD) left?.onTrigger();
    start.current = null;
    setDx(0);
  };

  const armed = Math.abs(dx) >= THRESHOLD;
  const shown = dx > 0 ? right : dx < 0 ? left : undefined;

  return (
    <div className={cx(s.wrap, className)}>
      {shown ? (
        <div
          className={s.action}
          data-side={dx > 0 ? 'right' : 'left'}
          data-tone={shown.tone}
          data-armed={armed || undefined}
          aria-hidden="true"
        >
          <Icon icon={shown.icon} size="lg" />
          <span>{shown.label}</span>
        </div>
      ) : null}
      <div
        className={s.content}
        style={{ '--dx': `${dx}px` } as CSSProperties}
        data-moving={dx !== 0 || undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {children}
      </div>
    </div>
  );
}
